import { C, S } from "./theme";
import { StatusBadge } from "./StatusBadge";
import { ADDRESS_TYPES, CUSTOMER_TYPES, PAYMENT_TERMS, VAT_TREATMENTS } from "../lib/customers";

const labelOf = (opts, v) => opts.find((o) => o.value === v)?.label || v || "—";
const Row = ({ k, v }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "6px 0", borderBottom: `1px solid ${C.borderSoft}` }}>
    <span style={{ color: C.textMuted, fontSize: 12 }}>{k}</span>
    <span style={{ fontSize: 13, textAlign: "right", wordBreak: "break-word" }}>{v || "—"}</span>
  </div>
);

export default function CustomerView({ customer, onClose, onEdit, onStatement }) {
  const c = customer;
  const contacts = c.customer_contacts || [];
  const addresses = c.customer_addresses || [];
  const timeline = c.customer_timeline_events || [];
  const money = (n) => (n == null ? "—" : `${c.preferred_currency || "EUR"} ${Number(n).toFixed(2)}`);

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.modalBox}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{c.company_name}</h2>
            <div style={{ color: C.textMuted, fontSize: 13, marginTop: 3 }}>
              {c.customer_number}{c.trading_name ? ` · t/a ${c.trading_name}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <StatusBadge status={c.status} />
            {onStatement && <button onClick={() => onStatement(c)} style={S.btn("secondary")}>Statement</button>}
            <button onClick={() => onEdit(c)} style={S.btn()}>Edit</button>
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 14 }}>
          <div>
            <div style={S.sectionTitle}>General</div>
            <Row k="Type" v={labelOf(CUSTOMER_TYPES, c.customer_type)} />
            <Row k="Industry" v={c.industry} />
            <Row k="VAT Number" v={c.vat_number} />
            <Row k="Company Reg." v={c.company_reg_number} />
            <Row k="Website" v={c.website} />
          </div>
          <div>
            <div style={S.sectionTitle}>Commercial</div>
            <Row k="Payment Terms" v={labelOf(PAYMENT_TERMS, c.payment_terms)} />
            <Row k="Credit Limit" v={money(c.credit_limit)} />
            <Row k="Currency" v={c.preferred_currency} />
            <Row k="VAT Treatment" v={labelOf(VAT_TREATMENTS, c.vat_treatment)} />
            <Row k="Customer Ref." v={c.customer_reference} />
          </div>
        </div>

        {c.notes && (
          <>
            <div style={S.sectionTitle}>Internal Notes</div>
            <div style={{ fontSize: 13, color: C.text, whiteSpace: "pre-wrap", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>{c.notes}</div>
          </>
        )}

        <div style={S.sectionTitle}>Contacts ({contacts.length})</div>
        {contacts.length === 0 ? <div style={{ color: C.textMuted, fontSize: 13 }}>No contacts.</div> : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {contacts.map((p) => (
              <div key={p.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, background: C.surface2 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  {`${p.first_name || ""} ${p.last_name || ""}`.trim() || "—"}
                  {p.is_primary && <span style={{ ...S.pill(C.primary), marginLeft: 8 }}>Primary</span>}
                </div>
                {p.job_title && <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 4 }}>{p.job_title}</div>}
                {p.email && <div style={{ fontSize: 12 }}>✉️ {p.email}</div>}
                {p.mobile && <div style={{ fontSize: 12 }}>📱 {p.mobile}</div>}
                {p.telephone && <div style={{ fontSize: 12 }}>☎️ {p.telephone}</div>}
              </div>
            ))}
          </div>
        )}

        <div style={S.sectionTitle}>Addresses ({addresses.length})</div>
        {addresses.length === 0 ? <div style={{ color: C.textMuted, fontSize: 13 }}>No addresses.</div> : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {addresses.map((a) => (
              <div key={a.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, background: C.surface2 }}>
                <div style={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", color: C.primary }}>
                  {labelOf(ADDRESS_TYPES, a.address_type)}{a.is_default ? " · default" : ""}{a.label ? ` · ${a.label}` : ""}
                </div>
                <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
                  {[a.line1, a.line2, a.city, a.county, a.postal_code, a.country].filter(Boolean).join(", ") || "—"}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Timeline structure exists; UI is a placeholder until later modules emit events. */}
        <div style={S.sectionTitle}>Activity Timeline</div>
        {timeline.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: 13, fontStyle: "italic" }}>
            No activity yet. Quotations, orders, invoices, deliveries, calls and emails will appear here.
          </div>
        ) : (
          <div>
            {timeline.map((e) => (
              <div key={e.id} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.borderSoft}` }}>
                <span style={S.pill(C.purple)}>{e.event_type}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{e.title || e.event_type}</div>
                  {e.body && <div style={{ fontSize: 12, color: C.textMuted }}>{e.body}</div>}
                  <div style={{ fontSize: 11, color: C.textDim }}>{new Date(e.occurred_at).toLocaleString("en-IE")}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
